import { logger } from "@/lib/logger";
import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useSiteContext } from "@/contexts/SiteContext";
import { TooltipWrapper } from "@/components/ui/tooltip-wrapper";
import { 
  Menu, 
  X, 
  Search, 
  User, 
  Users,
  Settings, 
  LogOut,
  Heart,
  Award,
  Briefcase,
  GraduationCap,
  BookOpen,
  MessageCircle,
  Stethoscope,
  FileCheck,
  BarChart3,
  FileText,
  TrendingUp,
  Shield,
  Globe,
  DollarSign,
  ChevronDown,
  Video,
  Brain,
  Zap,
  Coins,
  Network,
  Mail,
  Activity,
  Dna,
  ArrowRight,
  Building2,
  Target
} from "lucide-react";
import { OptimizedImage } from "@/components/optimization/ImageOptimizer";
import logoImage from "@/assets/ptc-logo.png";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { performCompleteLogout } from "@/lib/auth-utils";
import { useAuth } from "@/contexts/AuthContext";
import { OrganizationSwitcher } from "@/components/organization/OrganizationSwitcher";
import { ContextSwitcher } from "@/components/navigation/ContextSwitcher";
import { Badge } from "@/components/ui/badge";
import { ErrorBoundary } from "@/components/optimization/ErrorBoundary";
import { useUserContexts, UserContext } from "@/hooks/useUserContexts";
import { useNavigationItems, NavigationItem, PUBLIC_SOLUTIONS_ITEMS } from "@/hooks/useNavigationItems";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";

/** Render navigation items with optional group labels and separators */
function renderGroupedItems(items: NavigationItem[], navigate: (href: string) => void) {
  let lastGroup: string | undefined;
  const elements: React.ReactNode[] = [];
  
  items.forEach((item, idx) => {
    if (item.group && item.group !== lastGroup) {
      if (lastGroup !== undefined) {
        elements.push(<DropdownMenuSeparator key={`sep-${item.group}`} />);
      }
      elements.push(
        <DropdownMenuLabel key={`label-${item.group}`} className="text-xs text-muted-foreground uppercase tracking-wider">
          {item.group}
        </DropdownMenuLabel>
      );
      lastGroup = item.group;
    }
    const Icon = item.icon;
    elements.push(
      <DropdownMenuItem key={item.name + item.href} onClick={() => navigate(item.href)}>
        <Icon className="mr-2 h-4 w-4" />
        <span>{item.name}</span>
      </DropdownMenuItem>
    );
  });
  
  return elements;
}

interface NavigationProps {
  user?: any;
  userProfile?: any;
}

const getRoleLabel = (role: string | null | undefined, userProfile?: any): string => {
  const labels: Record<string, string> = {
    'patient_caregiver': 'Patient',
    'medical_professional': 'Medical Professional',
    'healthcare_provider': 'Healthcare Provider',
    'sponsor_service_provider': 'Sponsor',
    'cro': 'CRO',
    'trial_site': 'Trial Site',
    'admin': 'Administrator',
    'super_admin': 'Super Admin'
  };
  
  const baseRole = labels[role?.toLowerCase() || ''] || role || 'User';
  
  // Add organization context if available
  if (userProfile?.organization_name) {
    // For organizational roles, show org name primarily
    if (['sponsor_service_provider', 'cro', 'trial_site'].includes(role?.toLowerCase() || '')) {
      return userProfile.organization_name;
    }
    // For medical professionals with organizations
    if (['medical_professional', 'healthcare_provider'].includes(role?.toLowerCase() || '')) {
      const name = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim();
      if (name) {
        return `${name} - ${userProfile.organization_name}`;
      }
      return userProfile.organization_name;
    }
  }
  
  // For patients and individuals without org
  if (['patient_caregiver', 'medical_professional', 'healthcare_provider'].includes(role?.toLowerCase() || '')) {
    const name = `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim();
    if (name) return name;
  }
  
  return baseRole;
};

const getOrgTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    'sponsor': 'Sponsor',
    'cro': 'CRO',
    'site': 'Trial Site'
  };
  return labels[type] || type;
};

export function Navigation({ user: propsUser, userProfile: propsUserProfile }: NavigationProps = {}) {
  const { t } = useTranslation();
  // Use AuthContext for centralized auth state, fallback to props for backwards compatibility
  const authContext = useAuth();
  const { isAuthReady } = authContext || {};
  const user = propsUser ?? authContext?.user;
  const userProfile = propsUserProfile ?? authContext?.userProfile;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { activeContext, contexts } = useUserContexts();
  const location = useLocation();
  const siteContext = useSiteContext();
  const prevPathname = useRef(location.pathname);
  
  // Detect if we're on an authenticated route
  const isAuthenticatedRoute = 
    (location.pathname.startsWith('/trial-site/') || location.pathname === '/trial-site') || 
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/dashboard') ||
    location.pathname.startsWith('/profile') ||
    (location.pathname.startsWith('/cro/') || location.pathname === '/cro') ||
    location.pathname.startsWith('/sponsor');

  // Track route transitions to prevent phantom menu flash
  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      setIsTransitioning(true);
      prevPathname.current = location.pathname;
      
      const timer = setTimeout(() => {
        setIsTransitioning(false);
      }, 150);
      
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  const handleSignOut = async () => {
    if (isLoggingOut) return;
    
    setIsLoggingOut(true);
    
    // Timeout fallback - force redirect after 3 seconds
    const timeoutId = setTimeout(() => {
      logger.warn("Logout timeout - forcing redirect");
      navigate("/auth", { replace: true });
      window.location.reload();
    }, 3000);
    
    try {
      logger.debug("Starting logout process...");
      
      const { success, error } = await performCompleteLogout();
      
      clearTimeout(timeoutId);
      
      if (!success) {
        logger.error("Logout failed:", error);
        toast({
          title: "Error signing out",
          description: error || "Failed to sign out properly",
          variant: "destructive",
        });
        // Force redirect anyway on error
        navigate("/auth", { replace: true });
        return;
      }

      logger.debug("Logout successful, redirecting...");
      
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of your account.",
      });
      
      navigate("/auth", { replace: true });

    } catch (error) {
      clearTimeout(timeoutId);
      logger.error("Unexpected error during logout:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Redirecting...",
        variant: "destructive",
      });
      // Force redirect on error
      navigate("/auth", { replace: true });
    } finally {
      setIsLoggingOut(false);
      // Safety net: ensure redirect always happens
      setTimeout(() => {
        if (window.location.pathname !== "/auth") {
          window.location.replace("/auth");
        }
      }, 100);
    }
  };

  const getInitials = (firstName?: string, lastName?: string, email?: string) => {
    const initials = `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    if (initials) return initials;
    return email?.[0]?.toUpperCase() || 'U';
  };

  // Pass site context to navigation items hook for white-label subdomain support
  const navigationStructure = useNavigationItems(
    userProfile?.role, 
    authContext?.isAdmin, 
    activeContext,
    contexts,
    siteContext?.site ? {
      type: siteContext.site.type,
      id: siteContext.site.id,
      display_name: siteContext.site.display_name
    } : null
  );

  // Role-aware home link for logo - prioritize white-label subdomain context
  const effectiveRole = (authContext?.primaryRole || userProfile?.role || '').toLowerCase();
  
  // Determine home href: PRIORITY 1 = white-label subdomain, PRIORITY 2 = admin, PRIORITY 3 = role-based
  const getHomeHref = (): string => {
    // PRIORITY 1: White-label subdomain context overrides everything
    if (siteContext?.site?.type === 'phc') {
      return '/phc';
    }
    if (siteContext?.site?.type === 'cro') {
      return '/cro/command-center';
    }
    if (siteContext?.site?.type === 'trial_site' && siteContext?.site?.id) {
      return `/trial-site/${siteContext.site.id}`;
    }
    
    // PRIORITY 2: Admin users (only on main platform, not white-label)
    if (authContext?.isAdmin || effectiveRole === 'admin' || effectiveRole === 'super_admin') {
      return '/admin/command-center';
    }
    
    // PRIORITY 3: Role-based routing
    if (effectiveRole === 'sponsor' || effectiveRole === 'sponsor_service_provider') {
      return '/sponsor/command-center';
    }
    if (effectiveRole === 'cro') {
      return '/cro/command-center';
    }
    if (effectiveRole === 'trial_site' || effectiveRole === 'medical_professional' || effectiveRole === 'healthcare_provider') {
      return '/site-dashboard';
    }
    
    // Default: authenticated users go to patient dashboard, unauthenticated to home
    return user ? '/patient-dashboard' : '/home';
  };
  
  const homeHref = getHomeHref();

  // Wait for auth AND site context to be ready before rendering
  const isReady = isAuthReady && !siteContext?.isLoading;

  if (!isReady) {
    return (
      <nav className="bg-white/95 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 xl:px-8">
          <div className="flex justify-between py-3 sm:py-4 md:py-6">
            <div className="h-14 w-32 bg-muted/50 rounded animate-pulse" />
            <div className="hidden md:flex gap-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-8 w-20 bg-muted/50 rounded animate-pulse" />
              ))}
            </div>
            <div className="h-8 w-20 bg-muted/50 rounded animate-pulse" />
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="bg-white/95 backdrop-blur-sm border-b border-border sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 xl:px-8">
        <div className="flex justify-between py-3 sm:py-4 md:py-6">
          {/* Logo and brand - White-label support with trial site distinction */}
          <div className="flex items-center flex-shrink-0">
            <Link to={homeHref} className="flex items-center space-x-3 focus-ring">
              <img 
                src={siteContext?.site?.logo_url || logoImage} 
                alt={siteContext?.site ? `${siteContext.site.display_name} logo` : "Pharma Trial Connect logo - connecting patients with clinical trials"} 
                className="h-16 sm:h-20 md:h-24 lg:h-28 w-auto max-w-[320px] sm:max-w-[360px] md:max-w-[400px]"
              />
            </Link>
            {/* "Powered by PTC BRIDGE™" badge for trial sites */}
            {siteContext?.site && (
              <span className="hidden lg:flex ml-3 text-[10px] text-muted-foreground border border-border rounded-full px-2 py-0.5">
                Powered by PTC BRIDGE™
              </span>
            )}
          </div>

          {/* Desktop navigation */}
          <div className={`hidden md:flex items-center justify-center flex-1 min-w-0 gap-0.5 md:gap-1 lg:gap-2 xl:gap-4 mx-2 md:mx-3 xl:mx-4 transition-opacity duration-150 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
            {/* Public Navigation - Only show if NOT authenticated, NOT transitioning, and NOT on white-label subdomain */}
            {!isTransitioning && !user && !isAuthenticatedRoute && !siteContext?.site ? (
              <>
                {/* CONDENSED NAVIGATION for medium screens (768px-1279px) */}
                <div className="md:flex xl:hidden items-center gap-0.5 md:gap-1 lg:gap-2">
                  {/* Platform dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground hover:text-primary"
                        aria-label="Platform menu"
                      >
                        <Search className="h-4 w-4" />
                        <span className="whitespace-nowrap">Platform</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="start">
                      <DropdownMenuItem onClick={() => navigate("/clinical-trials")}>
                        <Search className="mr-2 h-4 w-4 text-primary" />
                        <span>
                          Browse Public Trials
                          <Badge className="ml-2 bg-primary/10 text-primary text-[9px] px-1">NEW</Badge>
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/bridge")}>
                        <Network className="mr-2 h-4 w-4 text-[hsl(var(--chart-5))]" />
                        <span>
                          BRIDGE™ - Africa Digital Layer
                          <Badge className="ml-2 bg-[hsl(var(--chart-5))]/10 text-[hsl(var(--chart-5))] text-[9px] px-1">LIVE</Badge>
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {navigationStructure.platform?.map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem key={item.name} onClick={() => navigate(item.href)}>
                            <Icon className="mr-2 h-4 w-4" />
                            <span>{item.name}</span>
                          </DropdownMenuItem>
                        );
                      })}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Browse by Region</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => navigate("/regions/united-states")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>United States</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/europe")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>Europe</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/africa")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>Africa</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/japan")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>Japan</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/china")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>China</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/india")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>India</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/australia")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>Australia & NZ</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/latin-america")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>Latin America</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Advanced AI dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground hover:text-primary"
                        aria-label="Advanced AI menu"
                      >
                        <Brain className="h-4 w-4" />
                        <span className="whitespace-nowrap">AI</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="start">
                      {navigationStructure.technology && renderGroupedItems(navigationStructure.technology, navigate)}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* MORE dropdown - Combines Solutions, Resources, Company */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground hover:text-primary"
                        aria-label="More menu"
                        title="Access solutions for trial sites, CROs, and sponsors, plus resources and company information"
                      >
                        <Users className="h-4 w-4" />
                        <span className="whitespace-nowrap">More</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 max-h-[min(85vh,32rem)] overflow-y-auto bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="start">
                      <DropdownMenuLabel>Solutions</DropdownMenuLabel>
                      {navigationStructure.solutions && renderGroupedItems(navigationStructure.solutions, navigate)}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Academy</DropdownMenuLabel>
                      {navigationStructure.academy && renderGroupedItems(navigationStructure.academy, navigate)}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Resources</DropdownMenuLabel>
                      {navigationStructure.resources && renderGroupedItems(navigationStructure.resources, navigate)}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Company</DropdownMenuLabel>
                      {navigationStructure.company?.map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem key={item.name} onClick={() => navigate(item.href)}>
                            <Icon className="mr-2 h-4 w-4" />
                            <span>{item.name}</span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {/* FULL NAVIGATION for large screens (1280px+) */}
                <div className="hidden xl:flex items-center gap-2 2xl:gap-4">
                  {/* Platform dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground hover:text-primary"
                        aria-label="Platform menu"
                      >
                        <Search className="h-4 w-4" />
                        <span className="whitespace-nowrap">Platform</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-56 bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="start">
                      <DropdownMenuItem onClick={() => navigate("/clinical-trials")}>
                        <Search className="mr-2 h-4 w-4 text-primary" />
                        <span>
                          Browse Public Trials
                          <Badge className="ml-2 bg-primary/10 text-primary text-[9px] px-1">NEW</Badge>
                        </span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      {navigationStructure.platform?.map((item) => {
                        const Icon = item.icon;
                        return (
                          <DropdownMenuItem key={item.name} onClick={() => navigate(item.href)}>
                            <Icon className="mr-2 h-4 w-4" />
                            <span>{item.name}</span>
                          </DropdownMenuItem>
                        );
                      })}
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Browse by Region</DropdownMenuLabel>
                      <DropdownMenuItem onClick={() => navigate("/regions/united-states")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>United States</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/europe")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>Europe</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/africa")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>Africa</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/japan")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>Japan</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/china")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>China</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/india")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>India</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/australia")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>Australia & NZ</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate("/regions/latin-america")}>
                        <Globe className="mr-2 h-4 w-4" />
                        <span>Latin America</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Conditions dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground hover:text-primary"
                        aria-label="Conditions menu"
                      >
                        <Heart className="h-4 w-4" />
                        <span className="whitespace-nowrap">Conditions</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-72 bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="start">
                      {/* Featured CTA at top */}
                      <div className="p-3 bg-green-50 border-b border-green-200 mb-2">
                        <Button 
                          className="w-full bg-green-600 hover:bg-green-700 text-white font-bold"
                          onClick={() => navigate("/register?userType=patient_caregiver")}
                        >
                          <Heart className="mr-2 h-4 w-4" />
                          Join Free - Find Your Trials
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                        <p className="text-xs text-green-700 mt-2 text-center">100% Free Forever · No Credit Card</p>
                      </div>
                      
                      <DropdownMenuLabel>Browse Conditions</DropdownMenuLabel>
                      {navigationStructure.conditions?.map((item) => {
                        const Icon = item.icon;
                        
                        // If item has subcategories, show expandable section
                        if (item.subcategories && item.subcategories.length > 0) {
                          return (
                            <div key={item.name} className="py-1">
                              <DropdownMenuItem 
                                onClick={() => navigate(item.href)}
                                className="font-semibold"
                              >
                                <Icon className="mr-2 h-4 w-4" />
                                <span>{item.name}</span>
                                <ChevronDown className="ml-auto h-3 w-3" />
                              </DropdownMenuItem>
                              <div className="ml-6 pl-2 border-l border-border/40">
                                {item.subcategories.map((sub) => (
                                  <DropdownMenuItem 
                                    key={sub.name} 
                                    onClick={() => navigate(sub.href)}
                                    className="text-sm py-1.5"
                                  >
                                    <ArrowRight className="mr-2 h-3 w-3 text-muted-foreground" />
                                    <span>{sub.name}</span>
                                  </DropdownMenuItem>
                                ))}
                              </div>
                            </div>
                          );
                        }
                        
                        // Regular item without subcategories
                        return (
                          <DropdownMenuItem key={item.name} onClick={() => navigate(item.href)}>
                            <Icon className="mr-2 h-4 w-4" />
                            <span>{item.name}</span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Advanced AI dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground hover:text-primary"
                        aria-label="Advanced AI menu"
                      >
                        <Brain className="h-4 w-4" />
                        <span className="whitespace-nowrap">Advanced AI</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="start">
                      {navigationStructure.technology && renderGroupedItems(navigationStructure.technology, navigate)}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Solutions dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground hover:text-primary"
                        aria-label="Solutions menu"
                      >
                        <Briefcase className="h-4 w-4" />
                        <span className="whitespace-nowrap">Solutions</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 max-h-[min(85vh,32rem)] overflow-y-auto bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="start">
                      {navigationStructure.solutions && renderGroupedItems(navigationStructure.solutions, navigate)}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Academy dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground hover:text-primary"
                        aria-label="Academy menu"
                      >
                        <GraduationCap className="h-4 w-4" />
                        <span className="whitespace-nowrap">Academy</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 max-h-[min(85vh,32rem)] overflow-y-auto bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="start">
                      {navigationStructure.academy && renderGroupedItems(navigationStructure.academy, navigate)}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  {/* Resources dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground hover:text-primary"
                        aria-label="Resources menu"
                      >
                        <BookOpen className="h-4 w-4" />
                        <span className="whitespace-nowrap">Resources</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 max-h-[min(85vh,32rem)] overflow-y-auto bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="start">
                      {navigationStructure.resources && renderGroupedItems(navigationStructure.resources, navigate)}
                    </DropdownMenuContent>
                  </DropdownMenu>

                </div>
              </>
            ) : !isTransitioning && user ? (
              <>
                {/* Authenticated User Navigation - filter out public nav categories on white-label sites */}
                {Object.entries(navigationStructure)
                  .filter(([groupKey]) => {
                    // On white-label sites, hide public marketing nav categories even if role is loading
                    if (siteContext?.site) {
                      const publicCategories = ['platform', 'conditions', 'technology', 'solutions', 'company'];
                      return !publicCategories.includes(groupKey);
                    }
                    return true;
                  })
                  .map(([groupKey, items]) => {
                  if (!items || !Array.isArray(items) || items.length === 0) return null;
                  
                  // Get group display name and icon
                  const getGroupInfo = (key: string) => {
                    switch (key) {
                      case 'myTrials': return { name: 'My Trials', icon: Heart };
                      case 'dashboard': return { name: 'Dashboard', icon: BarChart3 };
                      case 'patients': return { name: 'Patients', icon: Stethoscope };
                      case 'professionalTools': return { name: 'Professional Tools', icon: BarChart3 };
                      case 'trialManagement': return { name: 'Trial Management', icon: FileText };
                      case 'businessTools': return { name: 'Business Tools', icon: DollarSign };
                      case 'siteOperations': return { name: 'Site Operations', icon: Briefcase };
                      case 'patientManagement': return { name: 'Patient Management', icon: Users };
                      case 'croOperations': return { name: 'CRO Operations', icon: Building2 };
                      case 'analytics': return { name: 'Analytics', icon: BarChart3 };
                      case 'community': return { name: 'Community', icon: MessageCircle };
                      case 'resources': return { name: 'Resources', icon: GraduationCap };
                      case 'referralReview': return { name: 'Referral Review', icon: Target };
                      case 'main': return { name: 'Main', icon: Search };
                      case 'admin': return { name: 'Admin', icon: Shield };
                      case 'academy': return { name: 'Academy', icon: GraduationCap };
                      default: return { name: key, icon: Users };
                    }
                  };

                  const groupInfo = getGroupInfo(groupKey);
                  const GroupIcon = groupInfo.icon;

                  // Single items don't need dropdown
                  if (items.length === 1) {
                    const item = items[0];
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="flex items-center space-x-1 text-foreground hover:text-primary transition-colors focus-ring touch-target px-2 py-1"
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span className="hidden xl:inline whitespace-nowrap text-sm lg:text-base">{item.name}</span>
                      </Link>
                    );
                  }

                  // Multiple items get dropdown
                  return (
                    <DropdownMenu key={groupKey}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          className="flex items-center space-x-1 text-foreground hover:text-primary"
                          aria-label={`${groupInfo.name} menu`}
                        >
                          <GroupIcon className="h-4 w-4" />
                          <span className="whitespace-nowrap">{groupInfo.name}</span>
                          <ChevronDown className="h-3 w-3" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="w-64 max-h-[min(80vh,28rem)] overflow-y-auto bg-popover border border-border shadow-lg z-[999] backdrop-blur-sm p-1" align="start">
                        {items.map((item) => {
                          const Icon = item.icon;
                          return (
                            <DropdownMenuItem key={item.name} onClick={() => navigate(item.href)}>
                              <Icon className="mr-2 h-4 w-4" />
                              <span>{item.name}</span>
                            </DropdownMenuItem>
                          );
                        })}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                })}

                {/* Signed-in users on the main site still get the public Solutions menu
                    (role-based nav structures don't include it, which hid the Sponsor Journey) */}
                {!siteContext?.site && !navigationStructure.solutions && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="flex items-center space-x-1 text-foreground hover:text-primary"
                        aria-label="Solutions menu"
                      >
                        <Briefcase className="h-4 w-4" />
                        <span className="whitespace-nowrap">Solutions</span>
                        <ChevronDown className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="w-64 max-h-[min(85vh,32rem)] overflow-y-auto bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="start">
                      {renderGroupedItems(PUBLIC_SOLUTIONS_ITEMS, navigate)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </>
            ) : null}
          </div>

          {/* Right side - Profile or Auth buttons */}
          <div className="flex items-center space-x-2">
            {/* Language Switcher */}
            <LanguageSwitcher />
            
            {/* Only show context/org switchers on main platform, not on white-label subdomains */}
            {user && !siteContext?.site && (
              <ErrorBoundary fallback={null}>
                <ContextSwitcher />
              </ErrorBoundary>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-10 w-10 rounded-full">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={userProfile?.avatar_url || ""} alt="Profile" />
                      <AvatarFallback>
                        {getInitials(userProfile?.first_name, userProfile?.last_name, user.email)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56 bg-popover border border-border shadow-lg z-[100] backdrop-blur-sm" align="end" forceMount>
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-2">
                      <div>
                        <p className="text-sm font-medium leading-none">
                          {userProfile?.first_name && userProfile?.last_name 
                            ? `${userProfile.first_name} ${userProfile.last_name}`
                            : userProfile?.display_name || 'User'
                          }
                        </p>
                        <p className="text-xs leading-none text-muted-foreground mt-1">
                          {user.email}
                        </p>
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Badge variant="secondary" className="text-xs font-medium">
                          {getRoleLabel(userProfile?.role, userProfile)}
                        </Badge>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  
                  <DropdownMenuItem onClick={() => navigate("/profile")}>
                    <User className="mr-2 h-4 w-4" />
                    <span>{t('navigation.profile')}</span>
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={handleSignOut} 
                    disabled={isLoggingOut}
                    className="text-destructive focus:text-destructive"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>{isLoggingOut ? t('common.loading') : t('navigation.logout')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                variant="ghost"
                size="sm"
                className="inline-flex border border-foreground text-xs sm:text-sm px-2 sm:px-4"
                onClick={() => navigate("/auth")}
              >
                {t('navigation.login')}
              </Button>
            )}

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? "Close mobile menu" : "Open mobile menu"}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden animate-slide-in-right">
          <div className="px-3 pt-2 pb-16 space-y-1.5 bg-white border-t shadow-lg max-h-[calc(100vh-3.5rem)] overflow-y-auto scroll-smooth">
            {Object.entries(navigationStructure)
              .filter(([groupKey]) => {
                // On white-label sites, hide public marketing nav categories
                if (siteContext?.site) {
                  const publicCategories = ['platform', 'conditions', 'technology', 'solutions', 'company'];
                  return !publicCategories.includes(groupKey);
                }
                return true;
              })
              .map(([groupKey, items]) => {
              if (!items || !Array.isArray(items) || items.length === 0) return null;
              
              const getGroupName = (key: string) => {
                switch (key) {
                  case 'myTrials': return 'My Trials';
                  case 'patients': return 'Patients';
                  case 'professionalTools': return 'Professional Tools';
                  case 'trialManagement': return 'Trial Management';
                  case 'businessTools': return 'Business Tools';
                  case 'siteOperations': return 'Site Operations';
                  case 'patientManagement': return 'Patient Management';
                  case 'platform': return 'Platform';
                  case 'solutions': return 'Solutions';
                  case 'company': return 'Company';
                  case 'community': return 'Community';
                  case 'resources': return 'Resources';
                  case 'main': return 'Main';
                  case 'admin': return 'Admin';
                  case 'academy': return 'Academy';
                  default: return key;
                }
              };

              return (
                <div key={groupKey} className="space-y-1">
                  {items.length > 1 && (
                    <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2">
                      {getGroupName(groupKey)}
                    </div>
                  )}
                  {items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-secondary/50"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Icon className="h-5 w-5" />
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              );
            })}

            {/* Signed-in mobile: still show the public Solutions menu (Sponsor Journey etc.) */}
            {user && !siteContext?.site && !navigationStructure.solutions && (
              <div className="space-y-1">
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-3 py-2">
                  Solutions
                </div>
                {PUBLIC_SOLUTIONS_ITEMS.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className="flex items-center space-x-3 px-3 py-2 rounded-md text-base font-medium text-foreground hover:text-primary hover:bg-secondary/50"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}
              </div>
            )}

            {/* Mobile auth buttons */}
            {!user && (
              <div className="pt-4 space-y-2 px-3">
                {/* Primary: Sign In - Most prominent */}
                <Button 
                  className="w-full bg-primary hover:bg-primary/90 text-white font-bold min-h-[48px]" 
                  onClick={() => {
                    navigate("/auth");
                    setIsMobileMenuOpen(false);
                  }}
                >
                  {t('navigation.login')}
                </Button>
                
                {/* Secondary: Patient Registration - Hide on white-label sites */}
                {!siteContext?.site && (
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold min-h-[48px]" 
                    onClick={() => {
                      navigate("/register?userType=patient_caregiver");
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    <Heart className="mr-2 h-4 w-4" />
                    {t('navigation.register')}
                  </Button>
                )}
                
                {/* Tertiary: Business Users - Hide on white-label sites */}
                {!siteContext?.site && (
                  <Button 
                    variant="outline"
                    className="w-full min-h-[48px]" 
                    onClick={() => {
                      navigate("/register");
                      setIsMobileMenuOpen(false);
                    }}
                  >
                    {t('navigation.contact')}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}